'use client'

import { SolidFacebook } from '@qr-front/icons/solid-facebook'
import { cn } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { FacebookAuth } from './FacebookAuth'
import { socialButtonStyle } from './shared'

const FACEBOOK_APP_ID =
	process.env.REACT_APP_FACEBOOK_APP_ID || '852634732394583'

interface FacebookButtonProps {
	onClick?: () => void
	className?: string
	labelClassName?: string
}

const MyFacebookButton = ({ onClick, className }: FacebookButtonProps) => (
	<button
		type="button"
		onClick={onClick}
		className={cn(socialButtonStyle, className)}
	>
		<SolidFacebook className="shrink-0" />
	</button>
)

const MyFacebookButtonWithText = ({
	onClick,
	className,
	labelClassName,
}: FacebookButtonProps) => {
	const t = useTranslations()

	return (
		<button
			onClick={onClick}
			className={cn(socialButtonStyle, className)}
			type="button"
		>
			<SolidFacebook className="shrink-0" />
			<span className={labelClassName}>
				{t('auth__signup__social_facebook')}
			</span>
		</button>
	)
}

interface FacebookAuthResponse {
	accessToken: string
	data_access_expiration_time: number
	expiresIn: number
	signedRequest: string
	userID: string
	email?: string
	grantedScopes?: string
}

const isEmailScopeGranted = (
	response: Partial<FacebookAuthResponse>,
): boolean => Boolean(response.grantedScopes?.split(',').includes('email'))

interface FacebookBtnProps {
	onSubmit: (response: FacebookAuthResponse) => void
	onEmailDeclined?: () => void
	variant?: 'icon' | 'text'
	className?: string
	labelClassName?: string
}

export const FacebookAuthButton = ({
	onSubmit,
	onEmailDeclined,
	variant = 'icon',
	className,
	labelClassName,
}: FacebookBtnProps) => {
	const [isEmailDeclined, setIsEmailDeclined] = useState(false)

	const handleCallback = (response: FacebookAuthResponse) => {
		if (!response.accessToken) {
			onSubmit(response)
			return
		}

		if (!isEmailScopeGranted(response)) {
			setIsEmailDeclined(true)
			onEmailDeclined?.()
			return
		}

		setIsEmailDeclined(false)
		onSubmit(response)
	}

	return (
		<FacebookAuth
			isMobile={false}
			disableRedirect
			returnScopes
			reRequest={isEmailDeclined}
			appId={FACEBOOK_APP_ID}
			callback={handleCallback}
			component={
				variant === 'text'
					? (props: FacebookButtonProps) => (
							<MyFacebookButtonWithText
								{...props}
								className={className}
								labelClassName={labelClassName}
							/>
						)
					: (props: FacebookButtonProps) => (
							<MyFacebookButton
								{...props}
								className={className}
							/>
						)
			}
		/>
	)
}
